<template>
  <div>
    <div class="view-page">
      <title-bar title="Correos electrónicos" :slotData="true">
        <template slot="content-actions">
          <div class="users-search-control">
            <div class="users-search-control__input-wrap">
              <input
                class="input users-search-control__input"
                type="text"
                placeholder="Buscar correo..."
                v-model="search"
                @input="onSearch"
              >
              <button
                v-if="search"
                class="button users-search-control__clear"
                type="button"
                @click="clearSearch"
                aria-label="Limpiar búsqueda"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </template>
      </title-bar>
      <div class="table-container-v3">
        <table class="table table-v3 is-fullwidth is-striped">
          <colgroup>
            <col class="col-nombre" />
            <col class="col-origen" />
            <col class="col-remitente" />
            <col class="col-destinatarios" />
            <col class="col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Origen</th>
              <th>Remitente</th>
              <th>Destinatarios</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody v-if="isLoading">
            <tr v-for="n in 10" :key="n">
              <td><div class="-loading-text -w60"></div></td>
              <td><div class="-loading-text -w40"></div></td>
              <td><div class="-loading-text -w70"></div></td>
              <td><div class="-loading-text -w50"></div></td>
              <td><div class="-loading-text -w20"></div></td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-if="!emails.length">
              <td colspan="5" class="has-text-centered -text3 -gray">
                Sin correos configurados
              </td>
            </tr>
            <tr v-for="(item, index) in emails" :key="index">
              <td class="td-wrap">{{ item.emailType && item.emailType.name ? item.emailType.name : '-' }}</td>
              <td>
                <span v-if="item.isCustom" class="origin-chip origin-chip--custom">
                  <i class="fas fa-pencil-alt"></i> Personalizado
                </span>
                <span v-else class="origin-chip origin-chip--catalog">
                  <i class="fas fa-book"></i> Catálogo
                </span>
              </td>
              <td class="td-remitente td-wrap">{{ item.originAddress || 'alertas@intelligential.tech' }}</td>
              <td>
                <span v-if="item.destinations && item.destinations.length" class="destinations-cell">
                  <span class="destinations-cell__first">{{ item.destinations[0].address }}</span>
                  <span
                    v-if="item.destinations.length > 1"
                    class="destinations-cell__badge"
                    @mouseenter="tooltipIndex = index"
                    @mouseleave="tooltipIndex = null"
                  >
                    +{{ item.destinations.length - 1 }}
                    <div v-if="tooltipIndex === index" class="destinations-tooltip">
                      <div
                        v-for="dest in item.destinations.slice(1)"
                        :key="dest.id"
                        class="destinations-tooltip__row"
                      >
                        <span class="destinations-tooltip__address">{{ dest.address }}</span>
                        <span v-if="dest.mode" class="destinations-tooltip__mode">{{ modeLabel(dest.mode) }}</span>
                      </div>
                    </div>
                  </span>
                </span>
                <span v-else class="has-text-grey">—</span>
              </td>
              <td class="has-text-centered">
                <div class="dropdown">
                  <a role="button" aria-expanded="false" aria-label="menu" class="dropbtn">
                    <i class="fas fa-ellipsis-v" />
                  </a>
                  <div class="dropdown-content">
                    <a class="navbar-item" @click="openPreview(item)">
                      <i class="show-on-hover fas fa-chevron-right" />
                      Ver preview
                    </a>
                    <a v-if="canEdit && !item.isCustom" class="navbar-item" @click="openCustomize(item)">
                      <i class="show-on-hover fas fa-chevron-right" />
                      Personalizar
                    </a>
                    <a v-if="canEdit && item.isCustom" class="navbar-item" @click="openCustomize(item)">
                      <i class="show-on-hover fas fa-chevron-right" />
                      Editar
                    </a>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal: Preview -->
    <modal-component
      :active="showPreview"
      :titleModal="previewEmailName || 'Preview'"
      :withoutAction="true"
      :actionCenter="true"
      widthModal="680px"
      :scrollBody="true"
      @cancel="closePreview"
    >
      <div v-if="previewLoading" class="preview-loading">
        <div class="-loading-text -w50" style="margin-bottom: 14px; height: 16px;"></div>
        <div class="-loading-text -w100" style="height: 460px;"></div>
      </div>
      <div v-else>
        <div v-if="previewSubject" class="preview-subject">
          <span class="preview-subject__label">Asunto:</span>
          <span class="preview-subject__value">{{ previewSubject }}</span>
        </div>
        <iframe
          v-if="previewHtml"
          :srcdoc="previewHtml"
          sandbox="allow-same-origin"
          class="preview-iframe"
          title="Vista previa del correo electrónico"
        />
        <p v-else class="has-text-centered -gray preview-empty">Sin contenido disponible.</p>
      </div>
    </modal-component>

    <!-- Modal: Personalizar / Editar -->
    <modal-component
      :active="showCustomize"
      :titleModal="customizeItem && customizeItem.isCustom ? 'Editar correo' : 'Personalizar correo'"
      textSave="Guardar"
      widthModal="width:1060px"
      :scrollBody="false"
      :loading="isSaving"
      @cancel="closeCustomize"
      @hdlOnClick="saveCustomize"
    >
      <template v-if="customizeItem && customizeItem.isCustom" slot="footer">
        <button type="button" class="button" @click="openDefaultTemplate">
          <i class="fas fa-eye" style="margin-right: 6px;"></i>
          Ver template default
        </button>
      </template>

      <div class="customize-layout">
        <!-- Columna izquierda: Remitente + Destinatarios -->
        <div class="customize-layout__left">
          <!-- Remitente -->
          <div class="customize-section">
            <p class="customize-section__title">Remitente</p>
            <div class="field">
              <label class="label" for="sender-address">Remitente</label>
              <div class="control">
                <input
                  id="sender-address"
                  class="input"
                  :class="{
                    'is-warning': senderValidation === 'email-only',
                    'is-danger': senderValidation === 'invalid'
                  }"
                  type="text"
                  placeholder="Ej: Intelligential &lt;notificaciones@intelligential.tech&gt;"
                  v-model="customizeForm.senderAddress"
                >
              </div>
              <p v-if="senderValidation === 'email-only'" class="help is-warning">
                Agrega el nombre: <strong>Nombre &lt;correo@dominio.com&gt;</strong>
              </p>
              <p v-else-if="senderValidation === 'invalid'" class="help is-danger">
                Formato inválido. Usa: <strong>Nombre &lt;correo@dominio.com&gt;</strong>
              </p>
            </div>
          </div>

          <!-- Destinatarios -->
          <div class="customize-section customize-section--last">
            <p class="customize-section__title">Destinatarios</p>
            <div
              v-for="(dest, i) in customizeForm.destinations"
              :key="i"
              class="customize-dest-row"
            >
              <div class="select">
                <select v-model="dest.mode">
                  <option value="destination">TO</option>
                  <option value="cc">CC</option>
                  <option value="bcc">CCO</option>
                </select>
              </div>
              <input
                class="input"
                type="email"
                placeholder="correo@dominio.com"
                v-model="dest.address"
              >
              <button
                type="button"
                class="button is-light customize-dest-delete"
                @click="removeDestination(i)"
                aria-label="Eliminar destinatario"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
            <button type="button" class="button is-light customize-add-dest" @click="addDestination">
              <i class="fas fa-plus"></i>&nbsp; Agregar destinatario
            </button>
          </div>
        </div>

        <!-- Divisor vertical -->
        <div class="customize-layout__divider"></div>

        <!-- Columna derecha: HTML editor -->
        <div class="customize-layout__right">
          <p class="customize-section__title">Template HTML</p>
          <textarea
            class="textarea customize-html-editor"
            v-model="customizeForm.html"
            placeholder="Escribe el HTML del correo..."
          />
        </div>
      </div>
    </modal-component>

    <!-- Modal: Ver template default -->
    <modal-component
      :active="showDefaultTemplate"
      titleModal="Template default"
      :withoutAction="true"
      widthModal="680px"
      :scrollBody="true"
      @cancel="closeDefaultTemplate"
    >
      <template slot="footer">
        <button type="button" class="button is-danger-action" @click="restoreDefault">
          <i class="fas fa-undo" style="margin-right: 6px;"></i>
          Restaurar default
        </button>
      </template>
      <iframe
        v-if="defaultTemplateHtml"
        :srcdoc="defaultTemplateHtml"
        sandbox="allow-same-origin"
        class="preview-iframe"
        title="Template default"
      />
      <p v-else class="has-text-centered -gray preview-empty">Sin template default disponible.</p>
    </modal-component>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'EmailsUnified',
  components: {
    TitleBar: () => import('@/components/ui/TitleBar.vue'),
    ModalComponent: () => import('@/components/ui/ModalComponent')
  },
  data () {
    return {
      emails: [],
      isLoading: false,
      search: '',
      searchTimeout: null,
      tooltipIndex: null,
      // Preview
      showPreview: false,
      previewLoading: false,
      previewHtml: '',
      previewSubject: '',
      previewEmailName: '',
      previewVariables: [],
      previewResolvedVars: {},
      copied: false,
      // Personalizar
      showCustomize: false,
      isSaving: false,
      customizeItem: null,
      customizeForm: {
        senderAddress: '',
        destinations: [],
        html: ''
      },
      // Template default
      showDefaultTemplate: false
    }
  },
  computed: {
    ...mapState({
      role: state => state.financialCompany.role
    }),
    canEdit () {
      return this.role && ['admin', 'developer'].includes(this.role.code)
    },
    senderValidation () {
      const val = this.customizeForm.senderAddress.trim()
      if (!val) return null
      if (/^.+<[^\s@]+@[^\s@]+\.[^\s@]+>$/.test(val)) return 'valid'
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return 'email-only'
      return 'invalid'
    },
    defaultTemplateHtml () {
      return this.customizeItem && this.customizeItem.emailType
        ? this.customizeItem.emailType.template || ''
        : ''
    }
  },
  mounted () {
    this.loadEmails()
  },
  methods: {
    ...mapActions([
      'getFinancialEmails',
      'getEmailPreview',
      'saveFinancialEmailConfig',
      'saveFinancialEmailTemplate',
      'restoreDefaultEmailTemplate',
      'saveEmailDestination',
      'deleteEmailDestination'
    ]),
    loadEmails (search) {
      this.isLoading = true
      this.getFinancialEmails({ search: search || null }).then(result => {
        this.emails = result || []
        this.isLoading = false
      })
    },
    onSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadEmails(this.search || null)
      }, 400)
    },
    clearSearch () {
      this.search = ''
      this.loadEmails(null)
    },
    // Preview
    parseVariables (raw) {
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        console.error('Error al parsear variables del template:', e)
        return []
      }
    },
    parseResolvedVars (raw) {
      try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw
      } catch (e) {
        console.error('Error al parsear resolvedVars:', e)
        return {}
      }
    },
    openPreview (item) {
      this.showPreview = true
      this.previewLoading = true
      this.previewHtml = ''
      this.previewSubject = ''
      this.previewEmailName = item.emailType && item.emailType.name ? item.emailType.name : 'Preview'
      this.previewVariables = []
      this.previewResolvedVars = {}
      this.copied = false
      const emailTypeId = item.emailType && item.emailType.id
      this.getEmailPreview({ emailTypeId }).then(result => {
        if (result) {
          this.previewHtml = result.html || ''
          this.previewSubject = result.subject || ''
          this.previewVariables = result.variables ? this.parseVariables(result.variables) : []
          this.previewResolvedVars = result.resolvedVars ? this.parseResolvedVars(result.resolvedVars) : {}
        }
        this.previewLoading = false
      })
    },
    closePreview () {
      this.showPreview = false
      this.previewHtml = ''
      this.previewSubject = ''
      this.previewEmailName = ''
      this.previewVariables = []
      this.previewResolvedVars = {}
    },
    resolvedVarValue (variable) {
      const val = this.previewResolvedVars[variable]
      if (val === null || val === undefined || val === '') return '—'
      return val
    },
    copyVariable (variable) {
      const text = '${' + variable + '}'
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text)
      }
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
    modeLabel (mode) {
      const map = { destination: 'TO', cc: 'CC', bcc: 'CCO' }
      return map[mode] || mode
    },
    // Personalizar
    openCustomize (item) {
      this.customizeItem = item
      const destinations = item.destinations
        ? item.destinations.map(d => ({ id: d.id, mode: d.mode || 'destination', address: d.address }))
        : []
      const html = item.isCustom && item.templates && item.templates.length
        ? item.templates[0].template || ''
        : (item.emailType && item.emailType.template) || ''
      this.customizeForm = {
        senderAddress: item.originAddress || '',
        destinations,
        html
      }
      this.showCustomize = true
    },
    closeCustomize () {
      this.showCustomize = false
      this.customizeItem = null
      this.isSaving = false
    },
    addDestination () {
      this.customizeForm.destinations.push({ mode: 'destination', address: '' })
    },
    removeDestination (index) {
      this.customizeForm.destinations.splice(index, 1)
    },
    async saveCustomize () {
      this.isSaving = true
      const item = this.customizeItem
      const emailTypeId = item.emailType && item.emailType.id
      const originalDestinations = item.destinations || []
      const formDestinations = this.customizeForm.destinations

      const formIds = new Set(formDestinations.filter(d => d.id).map(d => d.id))

      const destinationSaves = formDestinations.map(dest =>
        this.saveEmailDestination({
          input: { emailTypeId, address: dest.address, mode: dest.mode, ...(dest.id ? { id: dest.id } : {}) }
        })
      )

      const destinationDeletes = originalDestinations
        .filter(orig => orig.id && !formIds.has(orig.id))
        .map(orig => this.deleteEmailDestination({ id: orig.id }))

      const calls = [
        this.saveFinancialEmailConfig({
          input: { emailTypeId, originAddress: this.customizeForm.senderAddress }
        }),
        this.saveFinancialEmailTemplate({
          input: { emailTypeId, template: this.customizeForm.html }
        }),
        ...destinationSaves,
        ...destinationDeletes
      ]

      await Promise.all(calls)
      this.isSaving = false
      this.closeCustomize()
      this.loadEmails()
    },
    // Template default
    openDefaultTemplate () {
      this.showDefaultTemplate = true
    },
    closeDefaultTemplate () {
      this.showDefaultTemplate = false
    },
    async restoreDefault () {
      const emailTypeId = this.customizeItem && this.customizeItem.emailType && this.customizeItem.emailType.id
      if (!emailTypeId) return
      await this.restoreDefaultEmailTemplate({ emailTypeId })
      this.closeDefaultTemplate()
      this.closeCustomize()
      this.loadEmails()
    }
  }
}
</script>

<style lang="scss" scoped>
// Column widths
.col-nombre        { width: 28%; }
.col-origen        { width: 13%; }
.col-remitente     { width: 26%; }
.col-destinatarios { width: 26%; }
.col-actions       { width: 7%; }

.td-wrap {
  white-space: normal;
  word-break: break-word;
}

.origin-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;

  &--catalog {
    background-color: #e8f0ff;
    color: #2d4ecf;
  }

  &--custom {
    background-color: #f0e8ff;
    color: #5b21b6;
  }
}

.td-remitente {
  font-size: 12px;
  color: #36475b;
}

.destinations-cell {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 4px;

  &__first {
    font-size: 13px;
    color: #36475b;
    word-break: break-all;
  }

  &__badge {
    position: relative;
    background-color: #e8f0ff;
    color: #2d4ecf;
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 20px;
    cursor: default;
    flex-shrink: 0;
  }
}

.destinations-tooltip {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #e0e3e9;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  min-width: 220px;
  z-index: 50;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    gap: 10px;
  }

  &__address {
    font-size: 12px;
    color: #36475b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
  }

  &__mode {
    font-size: 11px;
    font-weight: 600;
    background: #e8f0ff;
    color: #2d4ecf;
    padding: 1px 6px;
    border-radius: 4px;
    flex-shrink: 0;
  }
}

.users-search-control {
  display: flex;
  align-items: center;
  margin-right: 10px;
}
.users-search-control__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.users-search-control__input {
  width: 260px;
  height: 35px;
  border-radius: 999px;
  border: 1px solid #d0dae6;
  padding: 0 36px 0 14px;
  font-size: 13px;
}
.users-search-control__clear {
  position: absolute;
  right: 4px;
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #6b6d73;
  padding: 0;
  top: 50%;
  transform: translateY(-50%);
}

// Preview modal
.preview-subject {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 14px;

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: #7c7d7e;
    flex-shrink: 0;
  }

  &__value {
    font-size: 13px;
    color: #36475b;
  }
}

.preview-iframe {
  width: 100%;
  min-height: 480px;
  border: 1px solid #e0e3e9;
  border-radius: 6px;
  display: block;
}

.preview-empty {
  padding: 40px 0;
}

.preview-loading {
  padding: 8px 0;
}

// Personalizar modal — layout dos columnas
.customize-layout {
  display: flex;
  gap: 0;
  align-items: stretch;
  min-height: 460px;
}

.customize-layout__left {
  width: 300px;
  flex-shrink: 0;
  overflow-y: auto;
  padding-right: 20px;
}

.customize-layout__divider {
  width: 1px;
  background-color: #f0f0f0;
  flex-shrink: 0;
  margin: 0 20px;
}

.customize-layout__right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.customize-section {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;

  &--last {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  &__title {
    font-size: 11px;
    font-weight: 600;
    color: #7c7d7e;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 12px;
  }
}

.customize-dest-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .input {
    flex: 1;
    min-width: 0;
  }
}

.customize-dest-delete {
  flex-shrink: 0;
  color: #ff7979;
  border-color: transparent;

  &:hover {
    border-color: #ff7979;
    background: #fff5f5;
  }
}

.customize-add-dest {
  font-size: 12px;
  margin-top: 4px;
}

.customize-html-editor {
  flex: 1;
  font-family: monospace;
  font-size: 12px;
  resize: none;
  line-height: 1.6;
  min-height: 420px;
}
</style>
